import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface FilterContextType {
  selectedTenantIds: string[];
  selectedServerIds: string[];
  setSelectedTenantIds: (ids: string[]) => void;
  setSelectedServerIds: (ids: string[]) => void;
  toggleTenant: (tenantId: string) => void;
  toggleServer: (serverId: string) => void;
  selectAllTenants: (allTenantIds: string[]) => void;
  selectAllServers: (allServerIds: string[]) => void;
  clearTenantSelection: () => void;
  clearServerSelection: () => void;
  isTenantSelected: (tenantId: string) => boolean;
  isServerSelected: (serverId: string) => boolean;
  refreshTrigger: number;
  triggerRefresh: () => void;
  autoRefresh: boolean;
  setAutoRefresh: (enabled: boolean) => void;
  isRefreshing: boolean;
  setIsRefreshing: (isRefreshing: boolean) => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};

interface FilterProviderProps {
  children: ReactNode;
}

export const FilterProvider = ({ children }: FilterProviderProps) => {
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [selectedServerIds, setSelectedServerIds] = useState<string[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const triggerRefresh = useCallback(() => {
    setIsRefreshing(true);
    setRefreshTrigger((prev) => prev + 1);
    // isRefreshing'i 1 saniye sonra false yap (animasyon için)
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  }, []);

  const toggleTenant = useCallback((tenantId: string) => {
    setSelectedTenantIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]
    );
  }, []);

  const toggleServer = useCallback((serverId: string) => {
    setSelectedServerIds((prev) =>
      prev.includes(serverId) ? prev.filter((id) => id !== serverId) : [...prev, serverId]
    );
  }, []);

  const selectAllTenants = useCallback((allTenantIds: string[]) => {
    setSelectedTenantIds(allTenantIds);
  }, []);

  const selectAllServers = useCallback((allServerIds: string[]) => {
    setSelectedServerIds(allServerIds);
  }, []);

  const clearTenantSelection = useCallback(() => {
    setSelectedTenantIds([]);
  }, []);

  const clearServerSelection = useCallback(() => {
    setSelectedServerIds([]);
  }, []);

  const isTenantSelected = useCallback(
    (tenantId: string) => {
      return selectedTenantIds.includes(tenantId);
    },
    [selectedTenantIds]
  );

  const isServerSelected = useCallback(
    (serverId: string) => {
      return selectedServerIds.includes(serverId);
    },
    [selectedServerIds]
  );

  return (
    <FilterContext.Provider
      value={{
        selectedTenantIds,
        selectedServerIds,
        setSelectedTenantIds,
        setSelectedServerIds,
        toggleTenant,
        toggleServer,
        selectAllTenants,
        selectAllServers,
        clearTenantSelection,
        clearServerSelection,
        isTenantSelected,
        isServerSelected,
        refreshTrigger,
        triggerRefresh,
        autoRefresh,
        setAutoRefresh,
        isRefreshing,
        setIsRefreshing,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

